/**
 * Create Note Step Handler
 * Creates a note on the entity
 */

import { supabase } from '../../supabase';
import type {
  StepExecutionContext,
  StepExecutionResult,
  CreateNoteStepConfig
} from '../../types/workflow';

export async function executeCreateNote(
  context: StepExecutionContext
): Promise<StepExecutionResult> {
  try {
    const config = context.step.config as CreateNoteStepConfig;

    if (!config.note_template) {
      return {
        success: false,
        error: 'Missing required field: note_template',
      };
    }

    // Get entity data for variable substitution
    const entityData = context.entityData;

    // Replace variables in note template
    let content = config.note_template;
    content = content.replace(/\{entity_name\}/g, entityData.business_name || entityData.name || 'N/A');
    content = content.replace(/\{lead_name\}/g, entityData.business_name || 'N/A');
    content = content.replace(/\{deal_value\}/g, entityData.amount?.toString() || '0');
    content = content.replace(/\{old_state\}/g, context.entity.trigger_data?.old_state || 'N/A');
    content = content.replace(/\{new_state\}/g, context.entity.trigger_data?.new_state || 'N/A');
    content = content.replace(/\{email\}/g, entityData.email || 'N/A');
    content = content.replace(/\{phone\}/g, entityData.phone || 'N/A');
    content = content.replace(/\{lead_source\}/g, entityData.lead_source || 'N/A');
    content = content.replace(/\{contact_name\}/g, entityData.contact_name || 'N/A');

    // Create note
    const noteData: any = {
      content,
      user_id: context.userId,
      organization_id: context.organizationId,
      created_at: new Date().toISOString(),
      metadata: {
        source: 'workflow',
        workflow_id: context.workflow.id,
        workflow_name: context.workflow.name,
        step_id: context.step.id,
        step_name: context.step.name,
      },
    };

    // Link to appropriate entity
    if (context.workflow.entity_type === 'lead') {
      noteData.lead_id = context.entity.entity_id;
    } else if (context.workflow.entity_type === 'deal') {
      noteData.deal_id = context.entity.entity_id;
    } else if (context.workflow.entity_type === 'client') {
      noteData.client_id = context.entity.entity_id;
    }

    const { data: note, error } = await supabase
      .from('deal_notes')
      .insert(noteData)
      .select()
      .single();

    if (error) {
      console.error('Error creating note:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    console.log(`✅ Note created: ${note.id}`);

    return {
      success: true,
      nextStepId: context.step.next_step_id,
      data: { noteId: note.id },
    };
  } catch (error) {
    console.error('Error in createNote handler:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}
